import {
  Button,
  Icons,
  ProjectAvatar,
  surfaceRoleAttributes,
  surfaceRoleClassName,
} from "@/components/ui";
import { cn } from "@/lib/cn";
import type { MockProject } from "@/workshop/data/denker-presence-switcher";
import { WORK_ITEMS } from "@/workshop/data/denker-presence-switcher";

export function ActiveWorkDots({ count }: { count: number }) {
  if (count === 0) return null;
  return (
    <div className="flex items-center -space-x-1" aria-label={`${count} active work items`}>
      {WORK_ITEMS.slice(0, Math.min(count, WORK_ITEMS.length)).map((item) => (
        <span
          key={item.id}
          className={cn("h-2.5 w-2.5 rounded-full ring-2 ring-black/35", item.colorClass)}
          title={item.label}
        />
      ))}
    </div>
  );
}

export function SwitcherPanel({
  projects,
  selectedProjectId,
  selectedSpaceId,
  onSelectProject,
  onSelectSpace,
  onNewProject,
  onNewSpace,
}: {
  projects: MockProject[];
  selectedProjectId: string;
  selectedSpaceId: string;
  onSelectProject: (projectId: string) => void;
  onSelectSpace: (spaceId: string) => void;
  onNewProject: () => void;
  onNewSpace: () => void;
}) {
  const selectedProject =
    projects.find((project) => project.id === selectedProjectId) ?? projects[0]!;

  return (
    <div
      className={cn(
        surfaceRoleClassName("menu"),
        "absolute left-1/2 top-[86px] z-20 grid w-[520px] -translate-x-1/2 grid-cols-[190px_minmax(0,1fr)] overflow-hidden border border-glass-stroke-light p-2 shadow-glass",
      )}
      data-testid="denker-presence-switcher-panel"
      {...surfaceRoleAttributes("menu", {
        nativeLevel: "root",
        nativeGroup: "presence-switcher",
      })}
    >
      <div className="border-r border-glass-stroke-subtle pr-2">
        <div className="px-2 pb-2 text-appkit-mini font-semibold uppercase tracking-[0.14em] text-muted">
          Context
        </div>
        <div className="space-y-1">
          {projects.map((project) => (
            <button
              key={project.id}
              type="button"
              onClick={() => onSelectProject(project.id)}
              className={cn(
                "flex w-full items-center gap-2 rounded-[14px] px-2 py-2 text-left transition-colors",
                selectedProjectId === project.id
                  ? "bg-accent-muted text-primary"
                  : "text-secondary hover:bg-glass-fill-heavy",
              )}
              data-testid={`denker-presence-project-${project.id}`}
            >
              {project.kind === "home" ? (
                <span className="grid h-6 w-6 shrink-0 place-items-center rounded-control-sm bg-accent-muted text-accent">
                  <Icons.Sparkles className="h-3.5 w-3.5" />
                </span>
              ) : (
                <ProjectAvatar name={project.name} color={project.color} />
              )}
              <span className="min-w-0 flex-1 truncate text-appkit-caption font-medium">
                {project.name}
              </span>
            </button>
          ))}
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={onNewProject}
          className="mt-3 w-full justify-start"
          data-testid="denker-presence-new-project"
        >
          <Icons.Plus className="h-3.5 w-3.5" />
          New project
        </Button>
      </div>

      <div className="pl-2">
        <div className="flex items-center justify-between gap-3 px-2 pb-2">
          <div>
            <div className="text-appkit-caption font-semibold text-primary">
              {selectedProject.name}
            </div>
            <div className="text-appkit-mini text-muted">
              {selectedProject.kind === "home"
                ? "Default place for quick work"
                : "Project spaces"}
            </div>
          </div>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={onNewSpace}
            data-testid="denker-presence-new-space"
          >
            <Icons.Plus className="h-3.5 w-3.5" />
            Space
          </Button>
        </div>
        <div className="space-y-1">
          {selectedProject.spaces.map((space) => (
            <button
              key={space.id}
              type="button"
              onClick={() => onSelectSpace(space.id)}
              className={cn(
                "flex w-full items-center gap-3 rounded-[14px] px-3 py-2 text-left transition-colors",
                selectedSpaceId === space.id
                  ? "bg-glass-fill-heavy text-primary"
                  : "text-secondary hover:bg-glass-fill-heavy",
              )}
              data-testid={`denker-presence-space-${space.id}`}
            >
              <span
                className={cn(
                  "h-2.5 w-2.5 shrink-0 rounded-full",
                  selectedSpaceId === space.id
                    ? "bg-accent shadow-glow-accent-sm"
                    : "bg-muted",
                )}
              />
              <span className="min-w-0 flex-1 truncate text-appkit-caption font-medium">
                {space.name}
              </span>
              <ActiveWorkDots count={space.activeWork} />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
